// src/pages/PedidosAutomaticos/Log.js
import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import TableWithPagination from "../common/TableWithPagination";
import InfoCards from '../common/InfoCards';
import { Icon } from '@iconify/react';
import dayjs from 'dayjs';
import { useApi } from './../../hooks/useApi';
import ErrorPopover from '../common/ErrorPopover';
import DetalleLogPanel from './../salesorders/DetalleLogPanel';
import {useAuth} from './../../context/AuthContext';

const Log = () => {
  const getDefaultDateRange = () => {
    const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD
    return {
      from: `${today}T00:00:00`,
      to: `${today}T23:59:00`
    };
  };

  const { get } = useApi();
  const [logs, setLogs] = useState([]);
  const [dateRange, setDateRange] = useState(getDefaultDateRange());
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedProceso, setSelectedProceso] = useState('');
  const { user } = useAuth();


  const formatDate = (date) => (date ? dayjs(date).format('DD/MM/YYYY HH:mm') : '');
  // Paneles
  const [selectedLog, setSelectedLog] = useState(null);
  const [selectedTrad, setSelectedTrad] = useState(null);
  
  const [detalleLog, setDetalleLog] = useState(null);
  const [detalleTrad, setDetalleTrad] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);

  const cerrarPanel = () => {
    setSelectedLog(null);
    setDetalleLog(null);
  };

  const cerrarPanelTrad = () => {
    setDetalleTrad(null);
    setSelectedTrad(null);
  };

  const fetchDetalleLog = async (log) => {
    try {
      setLoadingDetalle(true);
      setSelectedLog(log);

      const params = { id: log.id };
      const data = await get('detalleLog', { params });
      
      setDetalleLog(data);
    } catch (error) {
      console.error('Error al cargar el detalle:', error);
      toast.error('Error al cargar el detalle del log');
    } finally {
      setLoadingDetalle(false);
    }
  };

  const fetchDetalleTraducido = async (traduccion) => { 
    try {
          setSelectedTrad(traduccion);
          const params = {
            fileName: traduccion.fileName,
            pipeLineExecutionId: traduccion.pipeLineExecutionId,
          };
          const data = await get('getSalesOrdersDet', { params });
          setDetalleTrad(data);
        } catch (error) {
          toast.error('Error al cargar el detalle del log');
        } 
  };

  const fetchFilteredLogs = async () => {
    try {
      const params = {
        customer: selectedProceso || undefined,
        fromDate: dateRange.from,
        toDate: dateRange.to,
        usuarioId: user?.id || undefined,
      };
      const data = await get('listaLogs', { params });
      setLogs(data);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Error al cargar los logs');
    }
  };

  useEffect(() => {
    fetchFilteredLogs();
  }, []);

  // Filtrar logs para tabla
  const filteredLogs = useMemo(() => {
    return logs.filter((log) => {
      const matchesCustomer = selectedProceso ? log.customerName === selectedProceso : true;
      const logDate = new Date(log.pipeLineExecutionStartDate);
      const matchesFrom = dateRange.from ? logDate >= new Date(dateRange.from) : true;
      const matchesTo = dateRange.to ? logDate <= new Date(dateRange.to) : true;
      const status = log.pipeLineExecutionStatus?.toLowerCase() || 'na';
      const matchesStatus = statusFilter ? status === statusFilter : true;
      return matchesCustomer && matchesFrom && matchesTo && matchesStatus;
    }).map((log) => ({
      ...log,
      pipeLineExecutionStartDate: formatDate(log.pipeLineExecutionStartDate),
      pipeLineExecutionEndDate: formatDate(log.pipeLineExecutionEndDate),
    }));
  }, [logs, selectedProceso, dateRange, statusFilter]);

  // Conteos de status
  const statusCounts = useMemo(() => {
    return logs
      .filter((log) => {
        const matchesProceso = selectedProceso ? log.customerName === selectedProceso : true;
        const logDate = new Date(log.pipeLineExecutionStartDate);
        const matchesFrom = dateRange.from ? logDate >= new Date(dateRange.from) : true;
        const matchesTo = dateRange.to ? logDate <= new Date(dateRange.to) : true;
        return matchesProceso && matchesFrom && matchesTo;
      })
      .reduce((acc, log) => {
        const status = log.pipeLineExecutionStatus?.toLowerCase() || 'na';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {});
  }, [logs, selectedProceso, dateRange]);

  const columnsResults = [
    //{ header: 'ID', accessor: 'id' },
    { header: 'Proceso', accessor: 'customerName' },
    {
      header: 'Archivo',
      accessor: 'fileName',
      Cell: ({ value }) => {
        if (!value) return '-';

        const maxLength = 50;
        const shortName =
          value.length > maxLength
            ? value.substring(0, maxLength) + '…'
            : value;

        return (
          <span title={value}>
            {shortName}
          </span>
        );
      }
    },
    {
      header: 'Orders',
      accessor: 'purchaseOrdersCount',
      cell: (value) => {
        const num = Number(value);
        return isNaN(num) ? 0 : num.toLocaleString('es-MX');
      },
    },
    { header: 'Fecha extracción', accessor: 'pipeLineExecutionStartDate', Cell: ({ value }) => value },
    //{ header: 'Fecha Fin', accessor: 'pipeLineExecutionEndDate', Cell: ({ value }) => value || '-' },
    {
      header: 'Extracción',
      accessor: 'pipeLineExecutionStatus',
      Cell: ({ value, row }) => (
        <div className="flex items-center gap-2">
          <ErrorPopover
            status={value}
            errorMessage={row.pipeLineExecutionError}
          />
          <Icon
            icon="mdi:information-outline"
            width={18}
            height={18}
            className="text-blue-500 cursor-pointer hover:text-blue-700"
            title="Ver detalle del proceso"
            onClick={() => fetchDetalleLog(row)}
          />
        </div>
      )
    },

    /**{
      header: 'Extracción',
      accessor: 'pipeLineExecutionStatus',
      Cell: ({ value, row }) => <ErrorPopover status={value} errorMessage={row.pipeLineExecutionError} />
    },*/
    {
      header: 'Traducción',
      accessor: 'orderStatusFinal',
      Cell: ({ row }) => {
        console.log('row for traducción:', row);
        const status = row.orderStatusFinal?.toLowerCase() || "";
        const exists = row.existsInSalesOrder === true;
        const OrderStatus = row.orderStatusFinal;

        // Si no tiene archivo, no mostrar nada
        if (status === "sinarchivo") {
          return null;
        }

        return (
          <div className="flex gap-2">
            {(
              <>
              <ErrorPopover
                status={row.orderStatusFinal}
                errorMessage={row.orderStatusFinal}
              />
              <Icon
                icon="mdi:information-outline"
                width={18}
                height={18}
                className="text-blue-500 cursor-pointer hover:text-blue-700"
                title="Ver detalle del proceso"
                onClick={() => fetchDetalleTraducido(row)}
              />
              </>
            )}
          </div>
        );
      },
    },
    { header: 'SAP', accessor: 'accion', 
      Cell: ({ row }) => {
        const status = row.pipeLineExecutionStatus?.toLowerCase() || "";
        const exists = row.existsInSalesOrder === true;

        // Si no tiene archivo, no mostrar nada
        if (status === "sinarchivo") {
          return null;
        }

        return (
          <div className="flex gap-2">
            {exists && (
              <>
              <span
                  className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium cursor-pointer bg-gray-100 text-gray-700 border border-gray-300`}
              >
                  <Icon icon="mdi:check-circle-outline" className="w-4 h-4 inline-block mr-1" />
                  <span>{'PTE.'}</span>
              </span>
              <Icon
                icon="mdi:information-outline"
                width={18}
                height={18}
                className="text-blue-500 cursor-pointer hover:text-blue-700"
                title="SAP"
              />
              </>
            )}
          </div>
        );
      },
    },



  ];

  const cardsData = [
    { label: 'Success', value: statusCounts.success || 0, icon: 'mdi:check-circle-outline', bgColor: 'bg-green-500', textColor: 'text-green-700', estatus: 'success' },
    { label: 'Error', value: statusCounts.error || 0, icon: 'mdi:alert-circle-outline', bgColor: 'bg-red-500', textColor: 'text-red-700', estatus: 'error' },
    { label: 'Sin Archivo', value: statusCounts.sinarchivo || 0, icon: 'mdi:alert-circle-outline', bgColor: 'bg-orange-500', textColor: 'text-orange-700', estatus: 'sinarchivo' },
    { label: 'Vacio', value: statusCounts.vacio || 0, icon: 'mdi:alert-circle-outline', bgColor: 'bg-purple-500', textColor: 'text-purple-700', estatus: 'vacio' },
    { label: 'N/A', value: statusCounts.na || 0, icon: 'mdi:help-circle-outline', bgColor: 'bg-gray-300', textColor: 'text-gray-700', estatus: 'na' },
  ];

  return (
    <div className="p-4">
      <div className="flex justify-between items-center pb-2 flex-wrap gap-2">
        <h1 className="text-xl font-bold">Pedidos Automáticos</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={selectedProceso}
            onChange={(e) => setSelectedProceso(e.target.value)}
            className="border rounded p-2"
          >
            <option value="">-- Todos los procesos --</option>
            {Array.from(new Set(logs.map(log => log.customerName))).map((customerName) => (
              <option key={customerName} value={customerName}>{customerName}</option>
            ))}
          </select>
          <input
            type="datetime-local"
            value={dateRange.from}
            onChange={(e) => setDateRange({ ...dateRange, from: e.target.value })}
            className="border rounded p-2"
            placeholder="Desde"
          />
          <input
            type="datetime-local"
            value={dateRange.to}
            onChange={(e) => setDateRange({ ...dateRange, to: e.target.value })}
            className="border rounded p-2"
            placeholder="Hasta"
          />
          <button
            onClick={() => fetchFilteredLogs()}
            className="px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center justify-center"
            title="Buscar"
          >
            <Icon icon="mdi:magnify" width="18" height="18" />
          </button>
        </div>
      </div>

      <div className="bg-white shadow-lg rounded-lg overflow-hidden">
        <div className="flex gap-4">
          {!selectedLog && !detalleTrad ? (
            <div className="flex-1 bg-white shadow-lg rounded-lg overflow-hidden">
              <div className="mb-4 pt-4 px-4">
                <InfoCards cardsData={cardsData} estatusActivo={statusFilter} onCardClick={setStatusFilter} />
              </div>
              <div className="p-4 text-xs overflow-x-auto">
                <div className="min-w-[800px]">
                  <TableWithPagination
                    columns={columnsResults}
                    data={filteredLogs}
                    showView={false}
                    showEdit={false}
                    showDelete={false}
                    loading={false}
                  />
                </div>
              </div>
            </div>
          ) : selectedLog ? (
            <div className="w-full bg-white shadow-lg rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-l font-semibold text-gray-800 flex items-center gap-2">
                  <span>📄</span> Log del Archivo {selectedLog?.fileName}
                </h3>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                  detalleLog?.pipeLineExecutionStatus === 'success' ? 'bg-green-100 text-green-800' :
                  detalleLog?.pipeLineExecutionStatus === 'error' ? 'bg-yellow-100 text-yellow-800' :
                  detalleLog?.pipeLineExecutionStatus === 'SINARCHIVO' ? 'bg-yellow-100 text-orange-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {detalleLog?.pipeLineExecutionStatus || 'N/A'}
                </span>
                <button
                  onClick={cerrarPanel}
                  className="mt-4 bg-gray-500 text-white px-3 py-1 rounded"
                >
                  Cerrar
                </button>
              </div>

              {loadingDetalle ? (
                <p className="text-gray-500 italic">Cargando detalle...</p>
              ) : detalleLog ? (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-gray-700 mt-4">
                    <div className="flex items-center gap-3 ">
                      <Icon icon="mdi:file-outline" className="text-blue-500 w-6 h-6" />
                      <div className='flex flex-column'>
                        <p className="text-sm text-gray-500">Archivo : </p>
                        <p className="font-semibold">{detalleLog.fileName}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 ">
                      <Icon icon="mdi:factory" className="text-purple-500 w-6 h-6" />
                      <div className='flex flex-column'>
                        <p className="text-sm text-gray-500">Proceso : </p>
                        <p className="font-semibold">{detalleLog.customerName}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 ">
                      <Icon icon="mdi:calendar-start" className="text-green-500 w-6 h-6" />
                      <div className='flex flex-column'>
                        <p className="text-sm text-gray-500">Fecha Inicio : </p>
                        <p className="font-semibold">{formatDate(detalleLog.pipeLineExecutionStartDate)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 ">
                      <Icon icon="mdi:calendar-end" className="text-red-500 w-6 h-6" />
                      <div className='flex flex-column'>
                        <p className="text-sm text-gray-500">Fecha Fin : </p>
                        <p className="font-semibold">{formatDate(detalleLog.pipeLineExecutionEndDate) || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 ">
                      <Icon icon="mdi:timer-outline" className="text-amber-500 w-6 h-6" />
                      <div className='flex flex-column'>
                        <p className="text-sm text-gray-500">Duración : </p>
                        <p className="font-semibold">{detalleLog.duracion}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 ">
                      <Icon icon="mdi:workflow" className="text-amber-500 w-6 h-6" />
                      <div className='flex flex-column'>
                        <p className="text-sm text-gray-500">pipeLine : </p>
                        <p className="font-semibold">{detalleLog.pipeLineExecutionId}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Icon
                        icon={detalleLog.detalles?.length > 0 ? 'mdi:check-circle-outline' : 'mdi:alert-circle-outline'}
                        className={`w-6 h-6 ${detalleLog.detalles?.length > 0 ? 'text-green-500' : 'text-red-500'}`}
                      />
                      <div className="flex flex-column">
                        <p className="text-sm text-gray-500">Registros encontrados:</p>
                        <p className="font-semibold">{detalleLog.detalles?.length || 0}</p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4 w-full overflow-x-auto">
                    <TableWithPagination 
                      columns={[
                        { header: 'PO Number', accessor: 'poNumber' },
                        { 
                          header: 'Estatus', 
                          accessor: 'pipeLineExecutionStatus',
                          Cell: ({ value, row }) => <ErrorPopover status={value} errorMessage={row.pipeLineExecutionError} />
                        },
                        { header: 'Rows Read', accessor: 'rowsRead' },
                      ]}
                      data={detalleLog.detalles || []}
                      showView={false}
                      showEdit={false}
                      showDelete={false}
                      loading={false}
                      initialSort={{ key: 'poNumber', direction: 'asc' }} 

                    />
                  </div>
                </>
              ) : (
                <p className="text-gray-500 italic">No se encontró información del detalle.</p>
              )}
            </div>
          ) : detalleTrad ? (
              <DetalleLogPanel
                log={selectedTrad}
                detalle={detalleTrad}
                loading={loadingDetalle}
                onClose={cerrarPanelTrad}
              />
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default Log;
